import axios from 'axios'
import * as PID from './Protocol'

const BASE_URL  = process.env.REACT_APP_API_URL ?? 'http://localhost:5071'
const ProcUrl   = `${BASE_URL}/Main/Proc`
const HealthUrl = `${BASE_URL}/Main/Health`
const DELIM = '\x18'

export interface ApiResult {
  resultCode:    string
  resultMessage: string
  resultData:    string
  rowCount:      number
  dataType:      number
  columnName:    string
  columnType:    string
}

async function httpPost(proc: string, requestData: string, token = ''): Promise<ApiResult> {
  const body = {
    token,
    sendTime:   new Date().toISOString().replace('T', ' ').slice(0, 19),
    proc,
    requestData,
    paramCount: requestData ? requestData.split(DELIM).length : 0,
  }
  const res = await axios.post<ApiResult>(ProcUrl, body)
  const data = res.data
  if (
    token &&
    (data.resultCode === '97' || data.resultMessage === '세션이 만료되었습니다.')
  ) {
    window.dispatchEvent(new CustomEvent('session-expired'))
  }
  return data
}

export async function apiHealth(): Promise<ApiResult> {
  const res = await axios.get<ApiResult>(HealthUrl)
  return res.data
}

// ── 사용자 ────────────────────────────────────────────────
export const apiUserRegister = (userId: string, userPass: string, userName: string) =>
  httpPost(PID.PID_USER_REGISTER, [userId, userPass, userName].join(DELIM))
export const apiUserLogin = (userId: string, userPass: string) =>
  httpPost(PID.PID_USER_LOGIN, [userId, userPass].join(DELIM))
export const apiUserLogout = (token: string) =>
  httpPost(PID.PID_USER_LOGOUT, '', token)
export const apiUserDupCheck = (userId: string) =>
  httpPost(PID.PID_USER_DUP_CHECK, userId)

// ── 구단 ──────────────────────────────────────────────────
export const apiClubCreate = (token: string, clubName: string, homeStadium: string) =>
  httpPost(PID.PID_CLUB_CREATE, [clubName, homeStadium].join(DELIM), token)
export const apiClubLoad = (token: string) =>
  httpPost(PID.PID_CLUB_LOAD, '', token)

// ── 선수 ──────────────────────────────────────────────────
export const apiPlayerList = (token: string, clubCode: number | string) =>
  httpPost(PID.PID_PLAYER_LIST, String(clubCode), token)
export const apiPlayerDetail = (token: string, playerCode: number | string) =>
  httpPost(PID.PID_PLAYER_DETAIL, String(playerCode), token)

// ── 일정/순위 ────────────────────────────────────────────
export const apiFixtureList = (
  token: string, leagueCode: number, seasonNo: number, roundFrom: number, roundTo: number,
) =>
  httpPost(PID.PID_FIXTURE_LIST, [leagueCode, seasonNo, roundFrom, roundTo].join(DELIM), token)
export const apiLeagueTable = (token: string, leagueCode: number, seasonNo: number) =>
  httpPost(PID.PID_LEAGUE_TABLE, [leagueCode, seasonNo].join(DELIM), token)

// ── 전술 (Phase 2/2.1) ───────────────────────────────────
export const apiTacticsLoad = (token: string, clubCode: number) =>
  httpPost(PID.PID_TACTICS_LOAD, String(clubCode), token)
export const apiTacticsSave = (
  token: string,
  clubCode: number,
  slotNo: number,
  slotName: string,
  formationCode: number,
  starting11Json: string,
  benchJson: string,
) =>
  httpPost(
    PID.PID_TACTICS_SAVE,
    [clubCode, slotNo, slotName, formationCode, starting11Json, benchJson].join(DELIM),
    token,
  )
export const apiTacticsActivate = (token: string, clubCode: number, slotNo: number) =>
  httpPost(PID.PID_TACTICS_ACTIVATE, [clubCode, slotNo].join(DELIM), token)

// ── 선수 사용 불가 (Phase 2.1) ───────────────────────────
export const apiPlayerStatusList = (token: string, clubCode: number) =>
  httpPost(PID.PID_PLAYER_STATUS_LIST, String(clubCode), token)
export const apiPlayerStatusUpsert = (
  token: string, playerCode: number, statusType: number, reason: string, returnDate: string,
) =>
  httpPost(
    PID.PID_PLAYER_STATUS_UPSERT,
    [playerCode, statusType, reason, returnDate].join(DELIM),
    token,
  )
export const apiPlayerStatusClear = (token: string, playerCode: number) =>
  httpPost(PID.PID_PLAYER_STATUS_CLEAR, String(playerCode), token)

// ── 라이브 매치 (Phase 2) ────────────────────────────────
export const apiMatchStartLive = (token: string, fixtureCode: number) =>
  httpPost(PID.PID_MATCH_START_LIVE, String(fixtureCode), token)
export const apiMatchLogList = (token: string, fixtureCode: number, afterLog: number) =>
  httpPost(PID.PID_MATCH_LOG_LIST, [fixtureCode, afterLog].join(DELIM), token)
export const apiMatchStats = (token: string, fixtureCode: number) =>
  httpPost(PID.PID_MATCH_STATS, String(fixtureCode), token)
// ── 경기 MoM·선수 평점 (Phase 8-C-5, 보상 영향 없음) ────────
export const apiMatchMomList = (token: string, fixtureCode: number) =>
  httpPost(PID.PID_MATCH_MOM_LIST, String(fixtureCode), token)
// ── 라이브 교체 콘솔 (Phase 8-C-2) ───────────────────────
export const apiMatchLiveConsole = (token: string, fixtureCode: number, clubCode: number) =>
  httpPost(PID.PID_MATCH_LIVE_CONSOLE, [fixtureCode, clubCode].join(DELIM), token)
export const apiMatchLiveSub = (
  token: string, fixtureCode: number, clubCode: number, outCode: number, inCode: number,
) =>
  httpPost(PID.PID_MATCH_LIVE_SUB, [fixtureCode, clubCode, outCode, inCode].join(DELIM), token)
export const apiMatchLiveFormation = (
  token: string, fixtureCode: number, clubCode: number, formationCode: number,
) =>
  httpPost(PID.PID_MATCH_LIVE_FORM, [fixtureCode, clubCode, formationCode].join(DELIM), token)
export const apiRoundAdvance = (
  token: string, leagueCode: number, seasonNo: number, roundNo: number,
) =>
  httpPost(PID.PID_ROUND_ADVANCE, [leagueCode, seasonNo, roundNo].join(DELIM), token)

// ── 이적시장 (Phase 3.1 + 6.2) ───────────────────────────
// scope: 0=LOCAL (사용자 NPC 매물), 1=GLOBAL (사용자 등록 매물 거래소)
export const apiTransferList = (
  token: string, leagueCode: number, scope: number, position: string, maxPrice: number, minOvr: number,
) =>
  httpPost(PID.PID_TRANSFER_LIST, [leagueCode, scope, position, maxPrice, minOvr].join(DELIM), token)

export const apiTransferBuy = (token: string, listingCode: number) =>
  httpPost(PID.PID_TRANSFER_BUY, String(listingCode), token)
export const apiTransferListUser = (token: string, playerCode: number, askPrice: number) =>
  httpPost(PID.PID_TRANSFER_LIST_USER, [playerCode, askPrice].join(DELIM), token)
export const apiTransferCancel = (token: string, listingCode: number) =>
  httpPost(PID.PID_TRANSFER_CANCEL, String(listingCode), token)
export const apiTransferBid = (token: string, listingCode: number, bidPrice: number) =>
  httpPost(PID.PID_TRANSFER_BID, [listingCode, bidPrice].join(DELIM), token)
export const apiTransferBidList = (token: string) =>
  httpPost(PID.PID_TRANSFER_BID_LIST, '', token)

// ── 재계약 (Phase 3.2) ───────────────────────────────────
export const apiRenewList = (token: string, clubCode: number) =>
  httpPost(PID.PID_RENEW_LIST, String(clubCode), token)
export const apiRenewSign = (token: string, playerCode: number, years: number) =>
  httpPost(PID.PID_RENEW_SIGN, [playerCode, years].join(DELIM), token)

// ── 주식 (Phase 4) ───────────────────────────────────────
export const apiStockList    = (token: string)                                     => httpPost(PID.PID_STOCK_LIST, '', token)
export const apiStockHistory = (token: string, stockCode: number)                  => httpPost(PID.PID_STOCK_HISTORY, String(stockCode), token)
export const apiStockBuy     = (token: string, stockCode: number, quantity: number) => httpPost(PID.PID_STOCK_BUY,  [stockCode, quantity].join(DELIM), token)
export const apiStockSell    = (token: string, stockCode: number, quantity: number) => httpPost(PID.PID_STOCK_SELL, [stockCode, quantity].join(DELIM), token)

// ── 은행 (Phase 5) ───────────────────────────────────────
export const apiLoanList     = (token: string, clubCode: number)                   => httpPost(PID.PID_LOAN_LIST,    String(clubCode), token)
export const apiLoanApply    = (token: string, amount: number)                      => httpPost(PID.PID_LOAN_APPLY,   String(amount), token)
export const apiLoanRepay    = (token: string, loanCode: number, amount: number)    => httpPost(PID.PID_LOAN_REPAY,   [loanCode, amount].join(DELIM), token)
export const apiDepositList  = (token: string, clubCode: number)                   => httpPost(PID.PID_DEPOSIT_LIST, String(clubCode), token)
export const apiDepositOpen  = (token: string, amount: number)                      => httpPost(PID.PID_DEPOSIT_OPEN, String(amount), token)
export const apiDepositClose = (token: string, depositCode: number)                => httpPost(PID.PID_DEPOSIT_CLOSE, String(depositCode), token)

// ── 컵대회 (Phase 6.3) ──────────────────────────────────
export const apiCupApply  = (token: string, eventDate: string) => httpPost(PID.PID_CUP_APPLY, eventDate, token)
export const apiCupCancel = (token: string, cupCode: number)   => httpPost(PID.PID_CUP_CANCEL, String(cupCode), token)
export const apiCupListMy = (token: string)                    => httpPost(PID.PID_CUP_LIST_MY, '', token)

// ── 챔피언스리그 (Phase 6.4) ────────────────────────────
export const apiChampionsApply = (token: string, eventDate: string) =>
  httpPost(PID.PID_CHAMPIONS_APPLY, eventDate, token)

// ── 재정 (Phase 3) ───────────────────────────────────────
export const apiFinanceLedger = (
  token: string, clubCode: number, seasonNo: number, monthFrom: number, monthTo: number,
) =>
  httpPost(PID.PID_FINANCE_LEDGER, [clubCode, seasonNo, monthFrom, monthTo].join(DELIM), token)
export const apiFinanceSummary = (token: string, clubCode: number, seasonNo: number) =>
  httpPost(PID.PID_FINANCE_SUMMARY, [clubCode, seasonNo].join(DELIM), token)

export const apiSetPieceStats = (token: string, leagueCode: number, seasonNo: number) =>
  httpPost(PID.PID_SETPIECE_STATS, [leagueCode, seasonNo].join(DELIM), token)

// ── 시즌 (Phase 2) ───────────────────────────────────────
export const apiSeasonEnd = (token: string, leagueCode: number) =>
  httpPost(PID.PID_SEASON_END, String(leagueCode), token)

// ── 뉴스 (발전 D) ────────────────────────────────────────
export const apiNewsList = (token: string, limit = 30) =>
  httpPost(PID.PID_NEWS_LIST, String(limit), token)
